import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { NotificationResponseDto } from "../models/notification/NotificationResponseDto";
import { employeeService } from "../pages/hrm/services/EmployeeService";

type NotificationWithAvatar = NotificationResponseDto & {
  actorAvatar?: string;
  actorAvatarId?: string;
  avatarId?: string;
};

const getDirectAvatar = (notification: NotificationResponseDto) => {
  const item = notification as NotificationWithAvatar;
  return item.actorAvatarId || item.actorAvatar || item.avatarId;
};

export const useNotificationActorAvatars = (notifications: NotificationResponseDto[]) => {
  const actorIds = useMemo(
    () =>
      Array.from(
        new Set(
          notifications
            .filter((notification) => !getDirectAvatar(notification))
            .map((notification) => notification.actorUserId)
            .filter((actorUserId): actorUserId is string => Boolean(actorUserId)),
        ),
      ),
    [notifications],
  );

  const queries = useQueries({
    queries: actorIds.map((actorUserId) => ({
      queryKey: ["employee-basic", actorUserId],
      queryFn: () => employeeService.getEmployeeBasic(actorUserId),
      staleTime: 1000 * 60 * 10,
    })),
  });

  return useMemo(() => {
    const map = new Map<string, string>();

    notifications.forEach((notification) => {
      const directAvatar = getDirectAvatar(notification);
      if (notification.actorUserId && directAvatar) {
        map.set(notification.actorUserId, directAvatar);
      }
    });

    queries.forEach((query, index) => {
      const actorUserId = actorIds[index];
      const avatarId = query.data?.avatarId;
      if (actorUserId && avatarId) {
        map.set(actorUserId, avatarId);
      }
    });

    return map;
  }, [actorIds, notifications, queries]);
};

export const getNotificationActorAvatar = (notification: NotificationResponseDto, avatarByActorId: Map<string, string>) => {
  const directAvatar = getDirectAvatar(notification);
  if (directAvatar) return directAvatar;
  if (!notification.actorUserId) return undefined;
  return avatarByActorId.get(notification.actorUserId);
};
